import type { QualificationKey, RunSummary } from "../shared/types";

export type SalesReadinessLevel = "won" | "offer" | "owned" | "ready" | "appointment" | "needs_appointment" | "nearly" | "progress" | "on_hold" | "closed";

export interface SalesReadiness {
  level: SalesReadinessLevel;
  label: string;
  reason: string;
  progress: string;
  missing: string[];
  rank: number;
}

const qualificationLabels: Record<QualificationKey, string> = {
  motivation: "Motivation",
  timeline: "Timeline",
  condition: "Condition",
  occupancy: "Occupancy",
  price: "Price",
  decisionMakers: "Decision-makers"
};

const qualificationKeys = Object.keys(qualificationLabels) as QualificationKey[];

type ReadinessRun = Pick<RunSummary, "appointmentAt" | "bucket" | "doNotContact" | "handoffOwner" | "leadDisposition" | "paused" | "qualification" | "qualificationCount" | "stage">;

const dispositionLabels = {
  not_interested: "Not interested",
  wrong_number: "Wrong number",
  already_sold: "Property sold",
  not_the_owner: "Not the owner"
} as const;

export function salesReadiness(run: ReadinessRun): SalesReadiness {
  const missing = qualificationKeys.filter((key) => !run.qualification[key]).map((key) => qualificationLabels[key]);
  if (!run.appointmentAt) missing.push("Team appointment");

  if (run.doNotContact || run.stage === "do_not_contact") {
    return {
      level: "closed",
      label: "Closed",
      reason: "The seller is marked do not contact, so this is no longer an active sales opportunity.",
      progress: "Outreach stopped",
      missing: [],
      rank: 0
    };
  }

  if (run.stage === "closed_not_fit") {
    return {
      level: "closed",
      label: "Not a fit",
      reason: "Acquisitions reviewed this seller opportunity and recorded why it was not a fit.",
      progress: "Closed by acquisitions",
      missing: [],
      rank: 0
    };
  }

  if (run.stage === "closed") {
    const label = run.leadDisposition ? dispositionLabels[run.leadDisposition] : "Closed lead";
    return {
      level: "closed",
      label,
      reason: `The team closed this lead as ${label.toLowerCase()}. Agents will not contact it unless a human reopens it.`,
      progress: "Outreach stopped",
      missing: [],
      rank: 0
    };
  }

  if (run.stage === "under_contract") {
    return {
      level: "won",
      label: "Under contract",
      reason: "Acquisitions converted this seller opportunity. Contract-to-close is the next team handoff.",
      progress: "Converted opportunity",
      missing: [],
      rank: 7
    };
  }

  if (run.stage === "offer_made") {
    return {
      level: "offer",
      label: "Offer made",
      reason: "Acquisitions delivered an offer and recorded a dated seller follow-up.",
      progress: "Offer follow-up scheduled",
      missing: [],
      rank: 6
    };
  }

  if (run.stage === "with_acquisitions" || run.bucket === "team") {
    const owner = run.handoffOwner ?? "The acquisitions team";
    return {
      level: "owned",
      label: "With acquisitions",
      reason: `${owner} owns this seller opportunity. AI outreach is paused while acquisitions prepares for the appointment.`,
      progress: `${run.qualificationCount}/6 facts · appointment booked`,
      missing: [],
      rank: 5
    };
  }

  if (run.stage === "nurture") {
    return {
      level: "on_hold",
      label: "Nurture scheduled",
      reason: "Hope has one dated seller check-in scheduled. Consent and every send-safety rule will be checked again before it leaves.",
      progress: "Dated check-in saved",
      missing,
      rank: 1
    };
  }

  if (run.stage === "handoff_ready") {
    return {
      level: "ready",
      label: "Ready for team",
      reason: "The seller facts and appointment are ready for a human review and handoff.",
      progress: `${run.qualificationCount}/6 facts · appointment booked`,
      missing,
      rank: 5
    };
  }

  if (run.paused || run.bucket === "held" || run.stage === "paused") {
    return {
      level: "on_hold",
      label: "On hold",
      reason: "Outreach is paused. Resume only when the team is ready for this conversation to continue.",
      progress: "Outreach paused",
      missing,
      rank: 0
    };
  }

  if (run.appointmentAt) {
    return {
      level: "appointment",
      label: "Appointment set",
      reason: missing.length ? "A team appointment is booked. The agents can keep filling the remaining seller facts before it starts." : "A team appointment is booked and all six seller facts are known.",
      progress: `${run.qualificationCount}/6 facts · appointment booked`,
      missing,
      rank: 4
    };
  }

  if (run.qualificationCount === 6) {
    return {
      level: "needs_appointment",
      label: "Needs appointment",
      reason: "All six seller facts are known. Booking the team appointment is the remaining step before handoff.",
      progress: "6/6 facts · appointment still needed",
      missing,
      rank: 3
    };
  }

  if (run.qualificationCount >= 4) {
    return {
      level: "nearly",
      label: "Nearly qualified",
      reason: `${run.qualificationCount} of 6 seller facts are known. The conversation is close to appointment-ready.`,
      progress: `${run.qualificationCount}/6 facts`,
      missing,
      rank: 2
    };
  }

  return {
    level: "progress",
    label: "Still qualifying",
    reason: `${run.qualificationCount} of 6 seller facts are known. The agents are still learning whether this should become a team appointment.`,
    progress: `${run.qualificationCount}/6 facts`,
    missing,
    rank: 1
  };
}
